import React from 'react';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';
import { ShadcnEntityCard } from './ShadcnEntityCard';
import { CommonPagination } from '../common/Pagination';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import type { EntityCardField } from './EntityCard';

export interface ShadcnCardGridProps {
    data: any[];
    entityType: string;
    isLoading: boolean;
    currentPage: number;
    totalResults: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onEntitySelect: (entity: any) => void;
    isPerson?: boolean;
    selectedEntityType?: string;
    visibleFields?: Set<string>;
    getCardFields?: (entity: any) => EntityCardField[];
}

const ShadcnCardGrid: React.FC<ShadcnCardGridProps> = ({
    data,
    entityType,
    isLoading,
    currentPage,
    totalResults,
    itemsPerPage,
    onPageChange,
    onEntitySelect,
    isPerson = false,
    selectedEntityType,
    visibleFields,
    getCardFields,
}) => {
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const singularLabel = getSingularLabel(entityType) || entityType;
    const pluralNoun = totalResults === 1
        ? singularLabel.toLowerCase()
        : (isPerson ? 'people' : `${singularLabel.toLowerCase()}s`);

    if (isLoading && data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                    Loading {isPerson ? 'people' : `${singularLabel.toLowerCase()}s`}...
                </span>
            </div>
        );
    }

    if (!isLoading && data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <span className="text-sm text-muted-foreground">No results found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 mt-4">
            {/* Top pagination */}
            {totalPages > 1 && (
                <CommonPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalResults={totalResults}
                    onPageChange={onPageChange}
                    itemsPerPage={itemsPerPage}
                    size="sm"
                />
            )}
            {totalPages <= 1 && totalResults > 0 && (
                <p className="text-xs text-muted-foreground text-center">
                    {totalResults.toLocaleString()} {pluralNoun}
                </p>
            )}

            {/* Card grid */}
            <div className="relative">
                {isLoading && data.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                )}
                <div
                    className={cn(
                        'grid gap-3',
                        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                        'transition-opacity duration-200',
                        isLoading && data.length > 0 && 'opacity-40 pointer-events-none',
                    )}
                >
                    {data.map((entity, idx) => (
                        <ShadcnEntityCard
                            key={entity?.Id || entity?.rawData?.Id || idx}
                            entity={entity}
                            entityType={entityType}
                            onSelect={onEntitySelect}
                            isPerson={isPerson}
                            selectedEntityType={selectedEntityType}
                            visibleFields={visibleFields}
                            fields={getCardFields?.(entity)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export { ShadcnCardGrid };
